import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  PhoneInTalk as PhoneInTalkIcon,
  LocalTaxi as TaxiIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import { useRideDialog } from '../rides/RideDialogProvider';
import {
  canReceiveTelephonyCalls,
  subscribeToIncomingCalls,
  toDate
} from '../../services/telephonyService';

const ACTIVE_CALL_MAX_AGE_MS = 10 * 60 * 1000;
const MAX_DISMISSED_CALLS = 100;

const dismissedCallsStorageKey = (userId) =>
  `nextcab.telephony.dismissedCalls.${userId || 'anonymous'}`;

const loadDismissedCallIds = (userId) => {
  if (!userId) return new Set();
  try {
    const storedIds = JSON.parse(localStorage.getItem(dismissedCallsStorageKey(userId)) || '[]');
    return new Set(Array.isArray(storedIds) ? storedIds.filter((id) => typeof id === 'string') : []);
  } catch (_) {
    return new Set();
  }
};

const saveDismissedCallIds = (userId, dismissedIds) => {
  if (!userId) return;
  try {
    const ids = Array.from(dismissedIds).slice(-MAX_DISMISSED_CALLS);
    localStorage.setItem(dismissedCallsStorageKey(userId), JSON.stringify(ids));
  } catch (_) {
    // Der Hinweis bleibt innerhalb der aktuellen Sitzung trotzdem ausgeblendet.
  }
};

const IncomingCallNotifier = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openRideDialog } = useRideDialog() || {};
  const user = useSelector(selectCurrentUser);
  const userId = user?.uid || '';
  const [calls, setCalls] = useState([]);
  const [error, setError] = useState('');
  const [dismissedIds, setDismissedIds] = useState(() => loadDismissedCallIds(userId));

  useEffect(() => {
    setDismissedIds(loadDismissedCallIds(userId));
  }, [userId]);

  useEffect(() => {
    if (!canReceiveTelephonyCalls(user)) return () => {};
    return subscribeToIncomingCalls(user, setCalls, (snapshotError) => {
      setError(snapshotError.message || 'Anrufe konnten nicht geladen werden.');
    });
  }, [user]);

  const activeCall = useMemo(() => calls.find((call) => {
    if (dismissedIds.has(call.id)) return false;
    if (!['ringing', 'connected', 'missed'].includes(call.state)) return false;
    const startedAt = toDate(call.startedAt);
    return startedAt && Date.now() - startedAt.getTime() <= ACTIVE_CALL_MAX_AGE_MS;
  }) || null, [calls, dismissedIds]);

  const dismiss = () => {
    if (!activeCall) return;
    setDismissedIds((current) => {
      const next = new Set(current);
      next.add(activeCall.id);
      saveDismissedCallIds(userId, next);
      return next;
    });
  };

  const prepareRide = () => {
    if (!activeCall) return;
    const customer = activeCall.matchedCustomer || null;
    const isDashboard = location.pathname === '/dashboard';

    if (isDashboard) {
      // Auf dem Dashboard: das kompakte Schnellformular in der LiveMap-Fahrtenliste
      // öffnen (vorbefüllt mit den Anruferdaten) – kein großer Dialog.
      navigate('/dashboard', {
        state: {
          openQuickRide: {
            incomingCallSessionId: activeCall.id,
            customerName: customer?.name || '',
            customerPhone: activeCall.callerNumberE164 || activeCall.callerNumberRaw || '',
            pickupAddress: customer?.address || ''
          }
        }
      });
    } else {
      // Woanders: den großen Fahrt-Dialog an Ort und Stelle öffnen, ohne wegzunavigieren.
      openRideDialog?.({
        mode: 'create',
        draft: {
          incomingCallDraft: {
            incomingCallSessionId: activeCall.id,
            customerId: customer?.id || '',
            customerName: customer?.name || '',
            customerPhone: activeCall.callerNumberE164 || activeCall.callerNumberRaw || '',
            pickupAddress: customer?.address || ''
          }
        }
      });
    }
    dismiss();
  };

  if (!canReceiveTelephonyCalls(user)) return null;

  return (
    <>
      <Snackbar
        open={Boolean(activeCall)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8, maxWidth: 440 }}
      >
        <Card
          elevation={8}
          sx={{
            width: '100%',
            borderLeft: 5,
            borderColor: activeCall?.source === 'ai_telephonist' ? 'info.main' : 'primary.main'
          }}
        >
          {activeCall && (() => {
            // KI-Telefonist-Anrufe: nur informativ anzeigen — die KI bearbeitet den
            // Anruf bereits, der Disponent muss (und soll) nichts vorbereiten.
            const isAiCall = activeCall.source === 'ai_telephonist';
            return (
            <>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  {isAiCall ? (
                    <SmartToyIcon color="info" sx={{ mt: 0.5 }} />
                  ) : (
                    <PhoneInTalkIcon color="primary" sx={{ mt: 0.5 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      color={isAiCall ? 'info.main' : 'primary'}
                      fontWeight="bold"
                    >
                      {isAiCall
                        ? '🤖 KI-Telefonist im Gespräch'
                        : activeCall.state === 'missed' ? 'Verpasster Anruf' : 'Eingehender Anruf'}
                    </Typography>
                    <Typography variant="h6">
                      {activeCall.matchedCustomer?.name ||
                        (activeCall.numberPresentation === 'withheld' ? 'Nummer unterdrückt' : 'Unbekannter Anrufer')}
                    </Typography>
                    <Typography variant="body1">
                      {activeCall.callerNumberE164 || activeCall.callerNumberRaw || 'Keine Rufnummer'}
                    </Typography>
                    {activeCall.matchedCustomer?.address && (
                      <Typography variant="body2" color="text.secondary">
                        {activeCall.matchedCustomer.address}
                      </Typography>
                    )}
                    {isAiCall && (
                      <Typography variant="body2" color="text.secondary">
                        Der KI-Telefonist bearbeitet diesen Anruf — kein Eingreifen nötig.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                {!isAiCall && (
                  <Button variant="contained" startIcon={<TaxiIcon />} onClick={prepareRide}>
                    Fahrt vorbereiten
                  </Button>
                )}
                {!isAiCall && activeCall.matchedCustomer?.id && (
                  <Button
                    startIcon={<PersonIcon />}
                    onClick={() => navigate(`/kunden/${activeCall.matchedCustomer.id}`)}
                  >
                    Kunde
                  </Button>
                )}
                <Button color="inherit" startIcon={<CloseIcon />} onClick={dismiss}>
                  Ausblenden
                </Button>
              </CardActions>
            </>
            );
          })()}
        </Card>
      </Snackbar>
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </>
  );
};

export default IncomingCallNotifier;
